import Paper from '@components/Atoms/Paper';
import React from 'react';
import Typography from '@components/Atoms/Typography';
import Box from '@components/Atoms/Box';
import ShowIf from '@components/Atoms/ShowIf';
import Button from '@components/Atoms/Button';
import ActionRequiredWrapper from '@contexts/ActionRequired/ActionRequiredWrapper';
import DateDisplay from '@components/Atoms/DateDisplay';
import {
  PlayArrow,
  Pause,
  Lock,
  CheckCircle,
  Timer,
  Assessment,
  Warning,
  Sync,
} from '@mui/icons-material';
import { StudentTestletSessionStatus, TestletStatus, Testlet, LockStrategy } from '../types';
import {
  useGetTestletSessions,
  useNavigateToLESessionReports,
  useNavigateToTestletSession,
} from '../hooks';
import { useCreateTestletSessionMutation } from '../mutations';

type TestletConfig = {
  header: string;
  label: string;
  icon: React.ReactNode;
};
const getTestletConfig = (isLocked: boolean, isCompleted: boolean, allowedToContinue: boolean): TestletConfig => {
  if (isCompleted) {
    return {
      header: 'Test Completed',
      label: 'View Report',
      icon: <CheckCircle />,
    };
  }

  if (isLocked && !allowedToContinue) {
    return {
      header: 'Test Locked',
      label: 'Test Locked',
      icon: <Lock />,
    };
  }

  return {
    header: 'Test is available',
    label: 'Start Test',
    icon: <PlayArrow />,
  };
};

const StudentTestCard = ({ testlet }: { testlet: Testlet }) => {
  const { data: testletSessions } = useGetTestletSessions(testlet?.id);
  const { mutate: createTestletSession, isLoading } = useCreateTestletSessionMutation(
    testlet.id,
  );
  const firstTestletSession = testletSessions?.[0];
  const testletSessionStatus =
  firstTestletSession?.status ?? StudentTestletSessionStatus.NOT_STARTED;

  const navigateToTestletSession = useNavigateToTestletSession();

  const handleStartSession = (testletId: number) => {
    if (!firstTestletSession) {
      createTestletSession(undefined, {
        onSuccess: ({ response }) => {
          if (response?.data?.id) {
            navigateToTestletSession(testletId);
          }
        },
      });
    } else {
      navigateToTestletSession(testletId);
    }
  };
  const navigateToLESessionReports = useNavigateToLESessionReports();

  // Determine the current state and action
  const isLocked =
    testlet.status === TestletStatus.LOCKED ||
    testlet.status === TestletStatus.NOT_STARTED ||
    testlet.status === TestletStatus.COMPLETED;
  const isCompleted = testletSessionStatus === StudentTestletSessionStatus.COMPLETED;
  const isInProgress = testletSessionStatus === StudentTestletSessionStatus.IN_PROGRESS;
  const isPaused = testletSessionStatus === StudentTestletSessionStatus.PAUSED;
  const isMissed = isCompleted && firstTestletSession?.started_at == null;
  const allowedToContinue =
    testlet.lock_strategy === LockStrategy.USE_REMAINING_TIME
    && (isInProgress || isPaused);

  // Detect force-completed sessions that never started (e.g., after time extension)
  // These should be treated as fresh starts, not resumes
  const isForceCompletedNeverStarted =
    isPaused &&
    firstTestletSession?.started_at == null &&
    firstTestletSession?.completed_at != null;

  // Get action button configuration
  const getActionConfig = () => {
    if (isLoading) {
      return {
        label: 'Loading...',
        icon: <Sync sx={{ animation: 'spin 2s linear infinite' }} />,
        color: 'primary' as const,
        variant: 'contained' as const,
        disabled: true,
        message: 'Preparing your test session',
        onClick: undefined,
      };
    }

    if (isCompleted) {
      return {
        label: 'View Report',
        icon: <CheckCircle />,
        color: 'success' as const,
        variant: 'outlined' as const,
        disabled: false,
        message: 'Test completed successfully',
        onClick: () => {
          navigateToLESessionReports();
        },
      };
    }
    if (isLocked && !allowedToContinue) {
      return {
        label: 'Test Locked',
        icon: <Lock />,
        color: 'error' as const,
        variant: 'outlined' as const,
        disabled: true,
        message: 'This test is currently locked by your instructor',
        onClick: undefined,
      };
    }

    // Handle force-completed sessions that never started - treat as fresh start
    if (isForceCompletedNeverStarted) {
      return {
        label: 'Start Test',
        icon: <PlayArrow />,
        color: 'primary' as const,
        variant: 'contained' as const,
        disabled: false,
        message: 'Begin your test now',
        onClick: () => {
          handleStartSession(testlet.id);
        },
      };
    }

    if (isInProgress || isPaused) {
      return {
        label: 'Resume Test',
        icon: <Pause />,
        color: 'primary' as const,
        variant: 'contained' as const,
        disabled: false,
        message: 'Continue where you left off',
        onClick: () => {
          handleStartSession(testlet.id);
        },
      };
    }

    // Test is unlocked and not started
    return {
      label: 'Start Test',
      icon: <PlayArrow />,
      color: 'primary' as const,
      variant: 'contained' as const,
      disabled: false,
      message: 'Begin your test now',
      onClick: () => {
        handleStartSession(testlet.id);
      },
    };
  };

  const testletConfig = getTestletConfig(isLocked, isCompleted, allowedToContinue);

  const actionConfig = getActionConfig();

  return (
    <Paper
      sx={{
        width: '100%',
        maxWidth: '420px',
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'grey.200',
        backgroundColor: 'white',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        },
      }}
      elevation={0}
      data-testid="student-test-card"
    >
      {/* Header with test name and status */}
      <Box
        sx={{
          padding: 4,
          borderBottom: '1px solid',
          borderColor: 'grey.100',
          backgroundColor: isLocked && !allowedToContinue && !isCompleted ? 'error.50' : 'success.50',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: isLocked && !allowedToContinue && !isCompleted
              ? 'linear-gradient(90deg, #f44336 0%, #d32f2f 100%)'
              : 'linear-gradient(90deg, #4caf50 0%, #388E3C 100%)',
          },
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{
            marginBottom: 2,
            textAlign: 'center',
            lineHeight: 1.3,
            fontSize: '2rem',
          }}
        >
          {testlet.name}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            color: isLocked && !allowedToContinue && !isCompleted ? 'error.main' : 'success.900',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: isLocked && !allowedToContinue && !isCompleted ? 'error.100' : 'success.100',
              color: isLocked && !allowedToContinue && !isCompleted ? 'error.main' : 'success.main',
            }}
          >
            {testletConfig.icon}
          </Box>
          <Typography
            variant="subtitle1"
            component="p"
            sx={{
              fontWeight: 600,
              color: isLocked && !allowedToContinue && !isCompleted ? 'error.main' : 'success.900',
              fontSize: '1.1rem',
            }}
          >
            {testletConfig.header}
          </Typography>
        </Box>
      </Box>

      {/* Main content */}
      <Box sx={{ padding: 4 }}>
        {/* Status message */}
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            marginBottom: 4,
            fontWeight: 500,
            fontSize: '1rem',
            lineHeight: 1.5,
            padding: 2,
            backgroundColor: 'grey.50',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'grey.100',
          }}
        >
          {actionConfig.message}
        </Typography>

        {/* Test information grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 3,
            marginBottom: 4,
          }}
        >
          {/* Duration for non-completed tests, Completion info for completed tests */}
          <Box
            sx={{
              padding: 3,
              backgroundColor: isMissed ? 'error.50' : isCompleted ? 'success.50' : 'primary.50',
              borderRadius: 4,
              textAlign: 'center',
              border: '2px solid',
              borderColor: isMissed ? 'error.100' : isCompleted ? 'success.100' : 'primary.100',
              transition: 'all 0.2s ease',
            }}
          >
            <ShowIf If={!isCompleted}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  marginBottom: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'primary.100',
                    color: 'primary.main',
                  }}
                >
                  <Timer sx={{ fontSize: '1.1rem' }} />
                </Box>
                <Typography
                  variant="subtitle2"
                  component="p"
                  sx={{ fontWeight: 600, color: 'primary.main' }}
                >
                  Duration
                </Typography>
              </Box>
              <Typography variant="h6" component="p" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {testlet.duration} min
              </Typography>
            </ShowIf>

            <ShowIf If={isCompleted}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  marginBottom: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: isMissed ? 'error.100' : 'success.100',
                    color: isMissed ? 'error.main' : 'success.main',
                  }}
                >
                  {isMissed ? (
                    <Warning sx={{ fontSize: '1.1rem' }} />
                  ) : (
                    <CheckCircle sx={{ fontSize: '1.1rem' }} />
                  )}
                </Box>
                <Typography
                  variant="subtitle2"
                  component="p"
                  sx={{ fontWeight: 600, color: isMissed ? 'error.main' : 'success.main' }}
                >
                  Status
                </Typography>
              </Box>
              <Typography
                variant="h6"
                component="p"
                sx={{ fontWeight: 700, color: isMissed ? 'error.main' : 'success.main' }}
              >
                {isMissed ? 'Missed' : 'Completed'}
              </Typography>
            </ShowIf>
          </Box>

          {/* Score - Only show when completed */}
          <ShowIf If={isCompleted}>
            <Box
              sx={{
                padding: 3,
                backgroundColor: isMissed ? 'error.50' : 'success.50',
                borderRadius: 4,
                textAlign: 'center',
                border: '2px solid',
                borderColor: isMissed ? 'error.100' : 'success.100',
                transition: 'all 0.2s ease',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  marginBottom: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: isMissed ? 'error.100' : 'success.100',
                    color: isMissed ? 'error.main' : 'success.main',
                  }}
                >
                  <Assessment sx={{ fontSize: '1.1rem' }} />
                </Box>
                <Typography
                  variant="subtitle2"
                  component="p"
                  sx={{ fontWeight: 600, color: isMissed ? 'error.main' : 'success.main' }}
                >
                  Score
                </Typography>
              </Box>

              <ShowIf If={testletSessions?.[0]?.score != null && !isMissed}>
                <Typography variant="h6" component="p" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {testletSessions?.[0]?.score} / {testletSessions?.[0]?.max_score}
                </Typography>
              </ShowIf>

              <ShowIf If={isMissed}>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{ fontWeight: 700, color: 'error.main' }}
                  data-testid="missed-score"
                >
                  -- / --
                </Typography>
              </ShowIf>

              <ShowIf If={testletSessions?.[0]?.score == null && !isMissed}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: 'warning.100',
                      color: 'warning.main',
                    }}
                  >
                    <Warning sx={{ fontSize: '0.9rem' }} />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: 'warning.main', fontWeight: 600 }}
                  >
                    Calculating...
                  </Typography>
                </Box>
              </ShowIf>
            </Box>
          </ShowIf>

          {/* Status info for non-completed tests */}
          <ShowIf If={!isCompleted}>
            <Box
              sx={{
                padding: 3,
                backgroundColor: isLocked && !allowedToContinue ? 'error.50' : 'grey.50',
                borderRadius: 4,
                textAlign: 'center',
                border: '2px solid',
                borderColor: isLocked && !allowedToContinue ? 'error.100' : 'grey.200',
                transition: 'all 0.2s ease',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  marginBottom: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: isLocked && !allowedToContinue ? 'error.100' : 'grey.200',
                    color: isLocked && !allowedToContinue ? 'error.main' : 'text.secondary',
                  }}
                >
                  {isLocked && !allowedToContinue ? (
                    <Lock sx={{ fontSize: '1.1rem' }} />
                  ) : (
                    <PlayArrow sx={{ fontSize: '1.1rem' }} />
                  )}
                </Box>
                <Typography
                  variant="subtitle2"
                  component="p"
                  sx={{
                    fontWeight: 600,
                    color: isLocked && !allowedToContinue ? 'error.main' : 'text.secondary',
                  }}
                >
                  Status
                </Typography>
              </Box>
              <Typography
                variant="h6"
                component="p"
                sx={{
                  fontWeight: 700,
                  color: isLocked && !allowedToContinue ? 'error.main' : 'text.secondary',
                }}
              >
                {isLocked && !allowedToContinue ? 'Locked' : isInProgress ? 'In Progress' : 'Ready to Start'}
              </Typography>
            </Box>
          </ShowIf>
        </Box>

        {/* Timestamp information */}
        <ShowIf If={testlet.status === TestletStatus.UNLOCKED}>
          <Box
            sx={{
              padding: 3,
              backgroundColor: 'grey.50',
              borderRadius: 4,
              marginBottom: 4,
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: '0.875rem',
              }}
            >
              Available since:{' '}
              <DateDisplay
                sx={{ border: 'none', justifyContent: 'center' }}
                startDate={testlet.unlocked_at}
                showTime
              />
            </Typography>
          </Box>
        </ShowIf>

        {/* Action button */}
        <ActionRequiredWrapper checkPath={['LE', +testlet.exam_session, +testlet.id]}>
          <Button
            sx={{
              width: '100%',
              height: '56px',
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 3,
              boxShadow: actionConfig.disabled ? 0 : '0 4px 12px rgba(0,0,0,0.15)',
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: actionConfig.disabled ? 0 : '0 6px 16px rgba(0,0,0,0.2)',
                transform: actionConfig.disabled ? 'none' : 'translateY(-1px)',
              },
              '&:disabled': {
                backgroundColor: 'grey.100',
                color: 'grey.500',
                borderColor: 'grey.300',
              },
            }}
            fullWidth
            variant={actionConfig.variant}
            color={actionConfig.color}
            className="actionable"
            disabled={actionConfig.disabled}
            startIcon={actionConfig.icon}
            onClick={actionConfig.onClick}
          >
            {actionConfig.label}
          </Button>
        </ActionRequiredWrapper>

        {/* Additional context for locked tests */}
        <ShowIf
          If={
            isLocked && testletSessionStatus === StudentTestletSessionStatus.NOT_STARTED
          }
        >
          <Box
            sx={{
              marginTop: 3,
              padding: 3,
              backgroundColor: 'error.50',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'error.100',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'error.main',
                textAlign: 'center',
                display: 'block',
                fontWeight: 600,
                fontStyle: 'italic',
                fontSize: '0.875rem',
              }}
            >
              Contact your instructor if you need access to this test
            </Typography>
          </Box>
        </ShowIf>
      </Box>
    </Paper>
  );
};

export default StudentTestCard;
